import {Application, ApplicationStatus} from '../types';
import {ApplicationInfrastructure} from '../types-infrastructure';
import {hasAnyRecordedInfrastructure, teardownTargetsFor} from './teardownTargets';

// FNP-998: can this application be decommissioned, and if not, why?
//
// Extracted from DecommissionButton so the decision is testable without a DOM.
// The console's test harness does not mount Mantine's ModalsProvider (see
// accounts/tests/buttons/RemoveRecordButton.test.tsx, which documents the same
// limitation), so modal CONTENT cannot be asserted — but this is the part that
// actually matters, and it is pure.
//
// An AFFORDANCE over the server's gate, not a second gate. The server is
// authoritative and refuses regardless (`ValidateDeprovisionable` is the first
// step of the state machine and writes its refusal to `statusReason`); this
// only chooses which sentence and which remedy to show. A UI that starts a run
// doomed to fail at its first step is worse than one that explains the problem
// next to the tab that fixes it.
//
// ⚠️ FNP-1601: IT READS THE INVENTORY, VIA `teardownTargetsFor`.
//
// This file used to say it "mirrors the server-side gate" and then read only
// `Application.infraStackNames`. The server had moved to
// `ApplicationInfrastructure` in FNP-1016 and this did not follow, so on an
// application provisioned TODAY — inventory row, no legacy fields — the console
// refused with "no stack name recorded" and sent the operator to type one on
// the Resource pointers tab: writing the OLD representation to satisfy a gate
// the server would already have passed from the NEW one.
//
// The ladder now lives in ONE place (`utils/teardownTargets.ts`) and that file
// names the server file it must track. A claim to mirror something is what let
// this drift; a shared seam is what stops it.

export type DecommissionBlocker =
    | 'NONE'
    | 'NO_STACK_NAME'
    | 'NEVER_PROVISIONED'
    | 'ALREADY_DELETING'
    | 'ALREADY_DECOMMISSIONED';

export interface DecommissionEligibility {
    eligible: boolean;
    blocker: DecommissionBlocker;
    // The stack that would be deleted. Only set when eligible — a caller must
    // never fall back to guessing one.
    stackName?: string;
    reason?: string;
    // What the admin should do about it, when there is something they can do.
    remedy?: string;
}

// FNP-1027: has this record demonstrably never had anything built for it?
//
// ⚠️ DECIDED ON RECORD STATE THE CONSOLE ALREADY HOLDS — never on a live AWS
// sweep. A client that swept and concluded "nothing exists" would be the
// fail-OPEN version of the very gate this file implements: a listing that
// failed, or one pointed at the wrong account, comes back empty and looks
// identical to genuine absence. Proving absence is the server's job
// (console-api's markApplicationDecommissioned, which refuses unless all four
// listings completed AND came back empty). This only chooses which sentence to
// show.
//
// ⚠️ CONSERVATIVE BY CONSTRUCTION. Every signal must be absent. One populated
// field means something was created, so the record falls through to today's
// message — the one that assumes infrastructure exists. The asymmetry is
// deliberate and matches the server's: wrongly showing "add the stack name" on
// an empty record wastes a step, while wrongly showing "just delete it" on a
// record that owns a live stack ORPHANS that stack. Only the first is
// recoverable, so uncertainty resolves to the old message.
//
// ⚠️ ONLY CREATING AND FAILED, per the ticket. A provision that never built
// anything leaves the record in one of those two. An ACTIVE record asserts the
// opposite — it came out of a completed run — so an ACTIVE record with nothing
// recorded is drift this cannot safely interpret, and it falls through to
// today's message. INACTIVE is a deliberate operator state on a real
// application, not a failed build. DELETING and DECOMMISSIONED never reach
// here; they are answered above.
// ⚠️ FNP-1601: THIS CHECKS BOTH REPRESENTATIONS, and it is the one place in
// this file where reading only one was actively DANGEROUS rather than merely
// unhelpful.
//
// The remedy attached to this label is "Delete this record instead." Reading
// only `Application.infraStackNames`, an application provisioned today —
// inventory row, no legacy fields — satisfied every clause below and was told
// to delete a record that owns live infrastructure. That inverts the very
// asymmetry the comment above argues for. `hasAnyRecordedInfrastructure`
// requires every signal from BOTH records to be absent.
function hasNeverBeenProvisioned(
    record: Application,
    infrastructure?: ApplicationInfrastructure | null,
): boolean {
    const status = record.applicationStatus;
    if (status !== ApplicationStatus.CREATING && status !== ApplicationStatus.FAILED) {
        return false;
    }

    // Covers stack names from EITHER record, both repositories, both pipeline
    // ids, the presence of an inventory row at all, and — because the
    // orchestrator stamps it the moment a run starts — `provisioningExecutionId`.
    // A record that HAS one may have died partway through with resources standing.
    return !hasAnyRecordedInfrastructure(record, infrastructure);
}

export function getDecommissionEligibility(
    record: Application,
    stage: 'dev' | 'test' | 'prod',
    // ⚠️ FNP-1601: OPTIONAL, and its absence means "no inventory row", never
    // "do not consult the inventory". Callers that have the record pass it;
    // one that does not degrades to the legacy behaviour rather than throwing.
    infrastructure?: ApplicationInfrastructure | null,
): DecommissionEligibility {
    if (record.applicationStatus === ApplicationStatus.DELETING) {
        return {
            eligible: false,
            blocker: 'ALREADY_DELETING',
            reason: 'A teardown is already running for this application.',
        };
    }

    if (record.applicationStatus === ApplicationStatus.DECOMMISSIONED) {
        return {
            eligible: false,
            blocker: 'ALREADY_DECOMMISSIONED',
            reason: 'This application has already been decommissioned.',
        };
    }

    // FNP-1601: from the INVENTORY when there is one, the `Application` map
    // when there is not — the same ladder the server walks, chosen once per
    // record rather than per field.
    const {targets} = teardownTargetsFor(record, infrastructure);
    const stackName = targets.filter((t) => t.stage === stage)[0]?.stackName ?? '';
    if (!stackName) {
        // FNP-1027: the refusal is right either way, but the REMEDY is not.
        //
        // "Add the stack name on the Provisioning tab" is correct when
        // infrastructure exists and the name was never recorded. On a record
        // that was never provisioned at all it is actively harmful: the admin
        // types a PLAUSIBLE name for a stack that does not exist, the teardown
        // finds nothing, and "found nothing" is exactly what the server-side
        // gate refuses to treat as success — because it is indistinguishable
        // from looking in the wrong account. The record ends up MORE stuck than
        // before, now carrying a pointer to a phantom stack.
        //
        // `river-joe` in test is the shape: CREATING, no repos, no pipelines,
        // no stack names, no execution. Every provisioning run that dies before
        // creating its first resource produces it.
        if (hasNeverBeenProvisioned(record, infrastructure)) {
            return {
                eligible: false,
                blocker: 'NEVER_PROVISIONED',
                reason: 'This application has no infrastructure recorded and no '
                    + 'provisioning run ever created any — no repositories, no '
                    + 'pipelines, no stack names. There is nothing to tear down.',
                remedy: 'Delete this record instead. Do not add a stack name: '
                    + 'naming a stack that does not exist would leave the record '
                    + 'pointing at infrastructure that was never created, and the '
                    + 'teardown would refuse it.',
            };
        }

        return {
            eligible: false,
            blocker: 'NO_STACK_NAME',
            reason: `No infrastructure stack name is recorded for the ${stage} environment, `
                + 'so the platform does not know which stack to delete. It will not guess: '
                + 'a stack it cannot find looks exactly like one that has already been '
                + 'deleted, and acting on that would report success while the application '
                + 'kept running.',
            // FNP-1588: names the tab as it now reads on screen. An
            // instruction pointing at a label that no longer exists is worse
            // than no instruction — the admin looks for "Provisioning", does
            // not find it, and has no way to tell whether the tab moved or
            // they are on the wrong page.
            //
            // ⚠️ FNP-1601: WHICH TAB DEPENDS ON WHICH RECORD IS AUTHORITATIVE.
            // Sending an operator to the Resource pointers tab when an
            // inventory row exists would have them repair the representation
            // the teardown is NOT reading — the fix would appear to do nothing.
            remedy: infrastructure
                ? 'Add the stack name for this environment on the Infrastructure '
                  + 'inventory tab, then try again.'
                : 'Add the stack name on the Resource pointers tab, then try again.',
        };
    }

    return {eligible: true, blocker: 'NONE', stackName};
}
